import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PORTAL_MAP, PORTAIS_COM_AUTOMACAO } from './procurementUi';
import { campo } from '../../design';

/**
 * Seletor de portal da licitação.
 *
 * ⛔ SUBSTITUI UM <select> NATIVO COM ~50 OPÇÕES CHAPADAS, que misturava três
 * perguntas diferentes numa lista só e por isso era impossível de usar na hora
 * de corrigir um portal à mão:
 *
 *   1. quais portais o HUB RECONHECE (as 39 chaves do PORTAL_MAP);
 *   2. em quais ele tem AUTOMAÇÃO de verdade — só quatro: Compras.gov.br, BLL,
 *      BNC e Portal Compras Públicas. Nos outros 35 a troca não muda robô
 *      nenhum, porque não há robô;
 *   3. em quais existe CREDENCIAL cadastrada em Acessos (o "✓").
 *
 * O estrago era o ✓ responder a pergunta 3 enquanto a escolha decide a 2. Um
 * portal sem automação mas com login aparecia com ✓; o Compras.gov.br, o mais
 * automatizado de todos, apareceria SEM ✓ se ninguém tivesse cadastrado a senha.
 * O confirm ainda avisava "muda qual automação o HUB usa" — verdade em 4 de 39
 * casos.
 *
 * Aqui os grupos separam pelo que a escolha FAZ, e o ✓ ganhou legenda dizendo o
 * que ele é. A busca existe porque lista de 50 sem filtro não se navega.
 */

interface PortalExtra {
  key: string;
  label: string;
}

interface Props {
  value: string | null;
  onChange: (key: string) => void;
  /** Chaves do PORTAL_MAP que têm credencial cadastrada em Acessos. */
  portalsWithAccess: Set<string>;
  /** Credenciais de Acessos que não casaram com nenhuma chave conhecida. */
  extraPortals: PortalExtra[];
  /** Opção para o valor atual quando ele não está em nenhuma das listas. */
  unmapped?: { value: string; label: string } | null;
  /** Classe de cor do rótulo atual (vem do PORTAL_MAP). */
  colorClass?: string;
  disabled?: boolean;
}

export default function PortalSelect({
  value,
  onChange,
  portalsWithAccess,
  extraPortals,
  unmapped,
  colorClass,
  disabled,
}: Props) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const caixaRef = useRef<HTMLDivElement>(null);
  const buscaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!aberto) return;
    // Foco na busca ao abrir: o ponto do componente é digitar o nome do portal
    // em vez de rolar 50 linhas.
    buscaRef.current?.focus();
    const fora = (e: MouseEvent) => {
      if (caixaRef.current && !caixaRef.current.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); setAberto(false); } // o Esc é do popover: a casca de fora não fecha junto
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);

  const rotuloAtual = useMemo(() => {
    if (!value) return 'Portal';
    if (PORTAL_MAP[value]) return PORTAL_MAP[value].label;
    const extra = extraPortals.find((e) => e.key === value);
    if (extra) return extra.label;
    return unmapped?.label || value;
  }, [value, extraPortals, unmapped]);

  const { comAutomacao, semAutomacao, extras } = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const casa = (label: string, key: string) =>
      !termo || label.toLowerCase().includes(termo) || key.toLowerCase().includes(termo);

    const auto: Array<{ key: string; label: string }> = [];
    const manual: Array<{ key: string; label: string }> = [];
    for (const [key, p] of Object.entries(PORTAL_MAP)) {
      if (!casa(p.label, key)) continue;
      (PORTAIS_COM_AUTOMACAO.has(key) ? auto : manual).push({ key, label: p.label });
    }
    // Dentro de cada grupo, alfabético: a ordem de declaração no código não diz
    // nada para quem procura um nome.
    auto.sort((a, b) => a.label.localeCompare(b.label));
    manual.sort((a, b) => a.label.localeCompare(b.label));
    const ex = extraPortals.filter((e) => casa(e.label, e.key));
    return { comAutomacao: auto, semAutomacao: manual, extras: ex };
  }, [busca, extraPortals]);

  const vazio = !comAutomacao.length && !semAutomacao.length && !extras.length;

  const Item = ({ item, temAcesso }: { item: { key: string; label: string }; temAcesso: boolean }) => (
    <button
      type="button"
      onClick={() => {
        setAberto(false);
        setBusca('');
        if (item.key !== value) onChange(item.key);
      }}
      className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 transition-colors ${
        item.key === value ? 'bg-slate-700 text-white font-semibold' : 'text-slate-300 hover:bg-slate-700/70'
      }`}
    >
      <span className={`w-3 shrink-0 ${temAcesso ? 'text-emerald-400' : 'text-transparent'}`}>✓</span>
      <span className="min-w-0 break-words">{item.label}</span>
    </button>
  );

  return (
    <div className="relative shrink-0" ref={caixaRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setAberto((v) => !v)}
        title="Portal de origem — alterar aqui muda a automação usada"
        className={`text-xs px-2 py-0.5 rounded-full border-0 cursor-pointer bg-slate-700/60 max-w-[190px] truncate disabled:opacity-50 ${colorClass || 'text-slate-400'}`}
      >
        {rotuloAtual} ▾
      </button>

      {aberto && (
        <div className="absolute z-50 mt-1 right-0 w-[290px] bg-slate-800 border border-slate-600 rounded-lg shadow-2xl overflow-hidden">
          <div className="p-2 border-b border-slate-700">
            <input
              ref={buscaRef}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar portal…"
              className={`w-full rounded px-2 py-1 text-xs outline-none ${campo}`}
            />
          </div>

          <div className="max-h-[300px] overflow-y-auto p-1.5 space-y-0.5">
            {vazio && <p className="text-xs text-slate-500 px-2 py-3 text-center">Nenhum portal encontrado</p>}

            {comAutomacao.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 px-2 pt-1 pb-0.5">
                  Com automação — muda scrape, envio e disputa
                </p>
                {comAutomacao.map((i) => (
                  <Item key={i.key} item={i} temAcesso={portalsWithAccess.has(i.key)} />
                ))}
              </>
            )}

            {semAutomacao.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 px-2 pt-2 pb-0.5">
                  Somente registro — o robô não opera
                </p>
                {semAutomacao.map((i) => (
                  <Item key={i.key} item={i} temAcesso={portalsWithAccess.has(i.key)} />
                ))}
              </>
            )}

            {extras.length > 0 && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 px-2 pt-2 pb-0.5">
                  Vindos de Acessos — sem automação
                </p>
                {extras.map((i) => (
                  <Item key={i.key} item={i} temAcesso />
                ))}
              </>
            )}

            {unmapped && (
              <>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 px-2 pt-2 pb-0.5">
                  Valor atual
                </p>
                <Item item={{ key: unmapped.value, label: unmapped.label }} temAcesso={false} />
              </>
            )}
          </div>

          {/* A legenda existe porque o ✓ era lido como "tem automação" — que é
              outra coisa, e é o que os grupos acima respondem. */}
          <div className="px-3 py-2 border-t border-slate-700 text-[10px] text-slate-500 leading-snug">
            <span className="text-emerald-400">✓</span> tem credencial cadastrada em{' '}
            <strong className="text-slate-400">Acessos</strong> — não significa que exista automação.
          </div>
        </div>
      )}
    </div>
  );
}
